import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import {Box, Paper, Stack} from "@mui/material";
import Typography from "@mui/material/Typography";
import {
    Survey,
    SurveyFormat,
    SurveyQuestionStyle,
    SurveyQuestionType
} from "crqlar-react-common/dist/models/survey/survey.model";
import {SurveyQuestion} from "crqlar-react-common/dist/models/survey/surveyQuestion.model";
import React from "react";
import {useTranslation} from "react-i18next";
import {SurveyQuestionTypeElement} from "../../common/uiElements/SurveyQuestionTypeElement";
import SurveyMarkdownField from "../shared/richText/SurveyMarkdownField";
import SurveyQuestionMarkers from "../shared/SurveyQuestionMarkers";
import {surveyPages} from "../shared/surveyPages.util";
import {isScreen} from "../shared/surveyQuestions.util";
import {getSurveyVariables} from "../shared/surveyVariables";
import ButtonLabelQuestionEditor from "./questions/ButtonLabelQuestionEditor";
import ChecklistQuestionEditor from "./questions/ChecklistQuestionEditor";
import ChoiceQuestionEditor from "./questions/ChoiceQuestionEditor";
import LegalQuestionEditor from "./questions/LegalQuestionEditor";
import {QuestionEditorProps} from "./questions/questionEditor.types";
import ScoreQuestionEditor from "./questions/ScoreQuestionEditor";
import StarsQuestionEditor from "./questions/StarsQuestionEditor";
import TextQuestionEditor from "./questions/TextQuestionEditor";
import YesNoQuestionEditor from "./questions/YesNoQuestionEditor";

interface Props {
    survey: Survey
    question?: SurveyQuestion
    language: string
    scaleLabelsVisible?: boolean
    onChange: (question: SurveyQuestion) => void
    disabled?: boolean
    /** One section per page: a request to focus the section heading field, handed to its `focusKey`. The editor
     *  sets it on a divider click and clears it on any other selection. */
    sectionTitleFocusKey?: number
}

const editorForType = (type: SurveyQuestionType | undefined) => {
    switch (type) {
        case SurveyQuestionType.MULTIPLE_CHOICE:
        case SurveyQuestionType.PICTURE_CHOICE:
        case SurveyQuestionType.DROPDOWN:
            return ChoiceQuestionEditor;

        case SurveyQuestionType.CHECKLIST:
            return ChecklistQuestionEditor;

        case SurveyQuestionType.GENERIC_SCORE:
            return ScoreQuestionEditor;

        case SurveyQuestionType.GENERIC_STARS:
            return StarsQuestionEditor;

        case SurveyQuestionType.YES_NO:
            return YesNoQuestionEditor;

        case SurveyQuestionType.SHORT_TEXT:
        case SurveyQuestionType.LONG_TEXT:
            return TextQuestionEditor;

        case SurveyQuestionType.LEGAL:
            return LegalQuestionEditor;

        case SurveyQuestionType.UPLOAD_MEDIA:
        case SurveyQuestionType.WELCOME:
        case SurveyQuestionType.END_SCREEN:
            return ButtonLabelQuestionEditor;

        // No canvas editor; their bounds are set under Configuration in the details panel.
        case SurveyQuestionType.DATE:
        case SurveyQuestionType.NUMBER:
            return undefined;

        case undefined:
            return undefined;

        default: {
            // A new enum member fails the typecheck here; a type from a newer API gets no editor.
            const unhandled: never = type;
            return undefined;
        }
    }
};

function SurveyEditorCanvas({
    survey,
    question,
    language,
    scaleLabelsVisible,
    onChange,
    disabled,
    sectionTitleFocusKey
}: Props) {
    const {t} = useTranslation();

    if (!question) {
        return (
            <Stack alignItems={'center'} justifyContent={'center'} sx={{flex: 1, minHeight: 240, p: 4}}>
                <Typography variant={'body2'} color={'text.secondary'} textAlign={'center'}>
                    {t('Select a question to edit it, or add a new one.')}
                </Typography>
            </Stack>
        );
    }

    const variables = getSurveyVariables(survey.type);
    const screen = isScreen(question);

    const editorProps: QuestionEditorProps = {
        question,
        patch: (partial) => onChange({...question, ...partial}),
        language,
        variables,
        disabled,
        scaleLabelsVisible,
    };

    const TypeEditor = editorForType(question.type);

    /*
        One section per page: the heading of the selected question's section, where guests see it. It is stored on
        the section's opening question, whichever question of the section is selected.
    */
    const sections = survey.format === SurveyFormat.SECTIONS && !screen
        ? surveyPages(survey.questions ?? [], SurveyFormat.SECTIONS)
        : [];
    const sectionIndex = sections.findIndex(page => page.questions.some(candidate => (
        candidate === question || (!!question.id && candidate.id === question.id)
    )));
    const opening = sections[sectionIndex]?.questions[0];

    const sectionHeading = !!opening && (
        <Stack gap={0.5}>
            <Typography variant={'body2'} color={'text.secondary'}>
                {t('Section {{n}}', {n: sectionIndex + 1})}
            </Typography>
            <SurveyMarkdownField
                variant={'heading'}
                value={opening.sectionTitle}
                language={language}
                onChange={(next) => onChange({...opening, sectionTitle: next})}
                placeholder={t('Section title')}
                variables={variables}
                disabled={disabled}
                focusKey={sectionTitleFocusKey}
            />
        </Stack>
    );

    const content = (
        <Stack gap={3}>
            <Stack gap={1}>
                {/* Read-only: the title and description are editable fields, so the flags a
                    question carries have nowhere to sit inside them. The panel still owns
                    changing them. Screens carry neither flag. */}
                {!screen && (
                    <Stack direction={'row'} alignItems={'center'} gap={1}>
                        <SurveyQuestionMarkers question={question} size={'standalone'} gap={0}/>
                        <SurveyQuestionTypeElement type={question.type}/>
                    </Stack>
                )}

                <SurveyMarkdownField
                    variant={'title'}
                    value={question.title}
                    language={language}
                    onChange={(next) => onChange({...question, title: next})}
                    placeholder={t('Question title')}
                    variables={variables}
                    disabled={disabled}
                />
                <SurveyMarkdownField
                    variant={'description'}
                    value={question.description}
                    language={language}
                    onChange={(next) => onChange({...question, description: next})}
                    placeholder={t('Add context or instructions for guests')}
                    variables={variables}
                    disabled={disabled}
                />
                {/*
                    Guests open it from the (?) after the title. Labelled, with the guest's glyph, because a filled
                    field would otherwise look just like the description above it. Screens carry none.
                */}
                {!screen && (
                    <Stack gap={0.5} sx={{mt: 1}}>
                        <Stack direction={'row'} alignItems={'center'} gap={0.5} sx={{color: 'text.secondary'}}>
                            <HelpOutlineOutlinedIcon sx={{fontSize: 16}}/>
                            <Typography variant={'body2'}>{t('Help text')}</Typography>
                        </Stack>
                        <SurveyMarkdownField
                            variant={'description'}
                            value={question.helpText}
                            language={language}
                            onChange={(next) => onChange({...question, helpText: next})}
                            placeholder={t('Add help guests can open with the ? button')}
                            variables={variables}
                            disabled={disabled}
                        />
                    </Stack>
                )}
            </Stack>

            {!!TypeEditor && <TypeEditor {...editorProps}/>}
        </Stack>
    );

    // Framed like the guest's cards, flat for Plain. Screens are no card for guests either.
    const framed = survey.theme?.questionStyle !== SurveyQuestionStyle.PLAIN && !screen;

    if (!framed) {
        return (
            <Box sx={{p: {xs: 2.5, sm: 4}}}>
                {sectionHeading ? <Stack gap={3}>{sectionHeading}{content}</Stack> : content}
            </Box>
        );
    }

    const card = (
        <Paper variant={'outlined'} sx={{borderRadius: 2, p: {xs: 2.5, sm: 4}}}>
            {content}
        </Paper>
    );

    // Above the card, outside its frame, as guests see a section's heading above its first question.
    return sectionHeading ? <Stack gap={3}>{sectionHeading}{card}</Stack> : card;
}

export default SurveyEditorCanvas;
